import React, { useState, useRef, useEffect } from 'react';
import { keyframes } from '@emotion/react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  Collapse,
  Backdrop
} from '@mui/material';
import { Close, ExpandMore, ExpandLess, DragIndicator } from '@mui/icons-material';

const blinkAttention = keyframes`
  0%, 100% { background-color: #1976d2; }
  50% { background-color: #90caf9; }
`;

const DataTableModal = ({ open, onClose, data, stationId }) => {
  // State for managing expanded rows
  const [expandedRows, setExpandedRows] = useState({});

  // State for minimize functionality — default to minimized
  const [isMinimized, setIsMinimized] = useState(true);

  // State for blinking attention indicator when minimized
  const [isBlinking, setIsBlinking] = useState(false);

  // Minimize and blink when modal opens with station data
  useEffect(() => {
    if (open && stationId && data) {
      setIsMinimized(true);
      setIsBlinking(true);
      const timer = setTimeout(() => setIsBlinking(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [open, stationId, data]);

  // State for drag and resize functionality
  const [width, setWidth] = useState(800);
  const [height, setHeight] = useState(600);
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ 
    x: typeof window !== 'undefined' ? Math.max(0, window.innerWidth / 2 - 100) : 400, 
    y: 100 
  });
  const [resizeDirection, setResizeDirection] = useState('');
  
  const containerRef = useRef(null);
  const startPosRef = useRef({ x: 0, y: 0 });
  const startSizeRef = useRef({ width: 0, height: 0 });
  const startPositionRef = useRef({ x: 0, y: 0 });
  const dragStartRef = useRef({ x: 0, y: 0 });

  // Handle resize
  const handleResizeMouseDown = (e, direction) => {
    setIsResizing(true);
    setResizeDirection(direction);
    startPosRef.current = { x: e.clientX, y: e.clientY };
    startSizeRef.current = { width, height };
    startPositionRef.current = { x: position.x, y: position.y };
    e.preventDefault();
    e.stopPropagation();
  };

  // Handle drag
  const handleDragMouseDown = (e) => {
    if (e.target.closest('.resize-handle') || e.target.closest('button')) return;
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizing) {
        const deltaX = e.clientX - startPosRef.current.x;
        const deltaY = e.clientY - startPosRef.current.y;
        
        if (resizeDirection.includes('right')) {
          const newWidth = Math.max(400, Math.min(1200, startSizeRef.current.width + deltaX));
          setWidth(newWidth);
        }
        if (resizeDirection.includes('left')) {
          const newWidth = Math.max(400, Math.min(1200, startSizeRef.current.width - deltaX));
          const actualWidthChange = newWidth - startSizeRef.current.width;
          setWidth(newWidth);
          // Move the modal to the right by the amount the width decreased
          setPosition(prev => ({
            ...prev,
            x: Math.max(0, startPositionRef.current.x - actualWidthChange)
          }));
        }
        if (resizeDirection.includes('bottom')) {
          const newHeight = Math.max(300, Math.min(800, startSizeRef.current.height + deltaY));
          setHeight(newHeight);
        }
      } else if (isDragging) {
        setPosition({
          x: Math.max(0, Math.min(window.innerWidth - width, e.clientX - dragStartRef.current.x)),
          y: Math.max(0, Math.min(window.innerHeight - height, e.clientY - dragStartRef.current.y))
        });
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setIsDragging(false);
      setResizeDirection('');
    };

    if (isResizing || isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, isDragging, position, width, height, resizeDirection]);

  // Define the columns to display
  const columns = [
    { field: 'masterid', label: 'Master ID', bold: true },
    { field: 'stationid_aliases', label: 'Station ID Aliases' },
    { field: 'refstatus_final', label: 'Ref Status Final', bold: true },
    { field: 'refstatus_comments', label: 'Ref Status Comments' },
    { field: 'refscreen_date', label: 'Ref Screen Date' },
    { field: 'flowstatus_final', label: 'Flow Status Final', bold: true },
    { field: 'flowstatus_comments', label: 'Flow Status Comments' },
    { field: 'flowstatus_date', label: 'Flow Status Date' },
    { field: 'latitude', label: 'Latitude' },
    { field: 'longitude', label: 'Longitude' },
    { field: 'regional_board', label: 'Regional Board' },
    { field: 'huc', label: 'HUC' },
    { field: 'county', label: 'County' },
    { field: 'channel_engineering_class', label: 'Channel Engineering Class' },
    { field: 'ref_gis_status', label: 'Ref GIS Status', expandable: true },
    { field: 'refgis_comments', label: 'Ref GIS Comments' },
    { field: 'refphab_status', label: 'Ref PHAB Status', expandable: true },
    { field: 'refphab_comments', label: 'Ref PHAB Comments' },
    { field: 'refbpj_status', label: 'Ref BPJ Status' },
    { field: 'refbpj_comments', label: 'Ref BPJ Comments' }
  ];

  // Sub-fields for expandable columns
  const subFields = {
    'ref_gis_status': [
      { field: 'ag_1k_16', label: 'AG 1K 16' },
      { field: 'ag_5k_16', label: 'AG 5K 16' },
      { field: 'ag_ws_16', label: 'AG WS 16' },
      { field: 'urban_1k_16', label: 'Urban 1K 16' },
      { field: 'urban_5k_16', label: 'Urban 5K 16' },
      { field: 'urban_ws_16', label: 'Urban WS 16' },
      { field: 'agur_1k_16', label: 'Ag+Urban 1K 16' },
      { field: 'agur_5k_16', label: 'Ag+Urban 5K 16' },
      { field: 'agur_ws_16', label: 'Ag+Urban WS 16' },
      { field: 'code_21_1k_16', label: 'Code 21 1K 16' },
      { field: 'code_21_5k_16', label: 'Code 21 5K 16' },
      { field: 'code_21_ws_16', label: 'Code 21 WS 16' },
      { field: 'developed_1k', label: 'Developed 1K' },
      { field: 'developed_5k', label: 'Developed 5K' },
      { field: 'developed_ws', label: 'Developed WS' },
      { field: 'roaddens_1k', label: 'Road Density 1K' },
      { field: 'roaddens_5k', label: 'Road Density 5K' },
      { field: 'roaddens_ws', label: 'Road Density WS' },
      { field: 'paved_int_1k', label: 'Paved Int 1K' },
      { field: 'paved_int_5k', label: 'Paved Int 5K' },
      { field: 'paved_int_ws', label: 'Paved Int WS' },
      { field: 'nrst_dam', label: 'Nearest Dam' },
      { field: 'cnl_pi_pct', label: 'Canal PI %' },
      { field: 'mine_dens', label: 'Mine Density' }
    ],
    'refphab_status': [
      { field: 'max_w1_hall_value', label: 'Max W1 Hall Value' },
      { field: 'max_w1_hall_date', label: 'Max W1 Hall Date' },
      { field: 'n_pass_phab', label: 'N Pass PHAB' },
      { field: 'n_fail_phab', label: 'N Fail PHAB' }
    ]
  };

  // Toggle expansion for a specific row
  const toggleExpand = (field) => {
    setExpandedRows(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Format value for display
  const formatValue = (value, field) => {
    if (value === null || value === undefined || value === 'None' || value === 'null' || value === '') {
      return '-';
    }
    // Format as integer for specific fields
    if (field === 'n_pass_phab' || field === 'n_fail_phab') {
      return parseInt(value, 10) || 0;
    }
    // Handle stationid_aliases field - could be array or comma-separated string
    if (field === 'stationid_aliases') {
      if (Array.isArray(value)) {
        return value.join(', ') || '-';
      }
      return value || '-';
    }
    // Format numeric fields with appropriate precision
    const numericFields = [
      'ag_1k_16', 'ag_5k_16', 'ag_ws_16',
      'urban_1k_16', 'urban_5k_16', 'urban_ws_16',
      'agur_1k_16', 'agur_5k_16', 'agur_ws_16',
      'code_21_1k_16', 'code_21_5k_16', 'code_21_ws_16',
      'developed_1k', 'developed_5k', 'developed_ws',
      'roaddens_1k', 'roaddens_5k', 'roaddens_ws',
      'paved_int_1k', 'paved_int_5k', 'paved_int_ws',
      'nrst_dam', 'cnl_pi_pct', 'mine_dens',
      'latitude', 'longitude'
    ];
    
    if (numericFields.includes(field)) {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        // For percentages and very small numbers, show more decimals
        if (field.includes('pct') || field.includes('dens') || Math.abs(num) < 1) {
          return num.toFixed(4);
        }
        // For coordinates
        if (field === 'latitude' || field === 'longitude') {
          return num.toFixed(6);
        }
        // For other numeric values
        return num.toFixed(2);
      }
    }
    return value;
  };

  return (
    <>
      {open && (
        <Paper 
          ref={containerRef}
          elevation={24}
          sx={{ 
            position: 'fixed',
            left: position.x,
            top: position.y,
            width: isMinimized ? 250 : width,
            height: isMinimized ? 'fit-content' : height,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            overflow: 'hidden',
            zIndex: 1001,
            cursor: isDragging ? 'grabbing' : 'default',
            userSelect: isDragging ? 'none' : 'auto',
            backgroundColor: 'background.paper',
            transition: isResizing || isDragging ? 'none' : 'width 0.3s ease, height 0.3s ease'
          }}
        >
          {/* Resize Handles */}
          {!isMinimized && (
            <>
              <Box
                className="resize-handle"
                onMouseDown={(e) => handleResizeMouseDown(e, 'right')}
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: 8,
                  cursor: 'ew-resize',
                  bgcolor: 'transparent',
                  '&:hover': {
                    bgcolor: 'primary.light',
                    opacity: 0.3
                  },
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <DragIndicator 
                  sx={{ 
                    fontSize: 16, 
                    color: 'text.secondary',
                    opacity: 0.5,
                    pointerEvents: 'none'
                  }} 
                />
              </Box>
              
              <Box
                className="resize-handle"
                onMouseDown={(e) => handleResizeMouseDown(e, 'bottom')}
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: 8,
                  cursor: 'ns-resize',
                  bgcolor: 'transparent',
                  '&:hover': {
                    bgcolor: 'primary.light',
                    opacity: 0.3
                  },
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <DragIndicator 
                  sx={{ 
                    fontSize: 16, 
                    color: 'text.secondary',
                    opacity: 0.5,
                    pointerEvents: 'none',
                    transform: 'rotate(90deg)'
                  }} 
                />
              </Box>
              
              <Box
                className="resize-handle"
                onMouseDown={(e) => handleResizeMouseDown(e, 'bottom-left')}
                sx={{
                  position: 'absolute',
                  left: 0,
                  bottom: 0,
                  width: 16,
                  height: 16,
                  cursor: 'ne-resize',
                  bgcolor: 'transparent',
                  '&:hover': {
                    bgcolor: 'primary.light',
                    opacity: 0.3
                  },
                  zIndex: 11,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <DragIndicator 
                  sx={{ 
                    fontSize: 12, 
                    color: 'text.secondary',
                    opacity: 0.6,
                    pointerEvents: 'none',
                    transform: 'rotate(45deg)'
                  }} 
                />
              </Box>
            </>
          )}
          
          {/* Header - Draggable */}
          <Box
            onMouseDown={handleDragMouseDown}
            onClick={() => {
              if (isMinimized) {
                setIsMinimized(false);
                setIsBlinking(false);
              }
            }}
            sx={{
              p: isMinimized ? 1 : 2,
              bgcolor: (isBlinking && isMinimized) ? undefined : 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: isMinimized ? 'center' : 'space-between',
              cursor: isDragging ? 'grabbing' : isMinimized ? 'pointer' : 'grab',
              borderBottom: 1,
              borderColor: 'divider',
              ...(isBlinking && isMinimized && {
                animation: `${blinkAttention} 0.8s ease-in-out infinite`,
                backgroundColor: '#1976d2'
              })
            }}
          >
            {!isMinimized && (
              <Typography variant="h6">
                Station Data: {stationId}
              </Typography>
            )}
            <IconButton
              size="small"
              sx={{ color: 'inherit' }}
              onClick={() => setIsMinimized(!isMinimized)}
              aria-label={isMinimized ? "expand" : "minimize"}
            >
              {isMinimized ? <ExpandMore /> : <ExpandLess />}
            </IconButton>
          </Box>
          
          {/* Content */}
          <Collapse in={!isMinimized} timeout="auto">
            <Box sx={{ 
              height: isMinimized ? 0 : height - 64, // Subtract header height
              overflow: 'auto', 
              p: 2 
            }}>
              {data ? (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '100%' }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            fontWeight: 'bold',
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            minWidth: 150
                          }}
                        >
                          Field
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            fontWeight: 'bold',
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText'
                          }}
                        >
                          Value
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {columns.map((column) => (
                        <React.Fragment key={column.field}>
                          <TableRow 
                            hover
                            onClick={column.expandable ? () => toggleExpand(column.field) : undefined}
                            sx={column.expandable ? { cursor: 'pointer' } : {}}
                          >
                            <TableCell 
                              component="th" 
                              scope="row"
                              sx={{ fontWeight: 'medium' }}
                            >
                              <Box display="flex" alignItems="center">
                                {column.expandable && (
                                  <IconButton
                                    size="small"
                                    sx={{ mr: 1 }}
                                  >
                                    {expandedRows[column.field] ? <ExpandLess /> : <ExpandMore />}
                                  </IconButton>
                                )}
                                {column.label}
                              </Box>
                            </TableCell>
                            <TableCell sx={column.bold ? { fontWeight: 'bold' } : {}}>
                              {formatValue(data[column.field], column.field)}
                            </TableCell>
                          </TableRow>
                          {column.expandable && subFields[column.field] && (
                            <TableRow>
                              <TableCell colSpan={2} sx={{ paddingBottom: 0, paddingTop: 0 }}>
                                <Collapse in={expandedRows[column.field]} timeout="auto" unmountOnExit>
                                  <Box sx={{ margin: 1 }}>
                                    <Table size="small">
                                      <TableBody>
                                        {subFields[column.field].map((subField) => (
                                          <TableRow key={subField.field}>
                                            <TableCell 
                                              component="th" 
                                              scope="row" 
                                              sx={{ pl: 6, fontWeight: 'medium', width: '40%' }}
                                            >
                                              {subField.label}
                                            </TableCell>
                                            <TableCell>
                                              {formatValue(data[subField.field], subField.field)}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No station being selected
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </Paper>
      )}
    </>
  );
};

export default DataTableModal;